import React, { useState } from 'react';
import { GroupChatModel, WelcomeMessageModel, CoachModel } from '../../types/group';
import { GroupsService } from '../../services/groupsService';
import Modal from '../../Common/Components/Modal';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';

// Simple UUID generator without external dependency
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

interface AddGroupDialogProps {
  onClose: () => void;
  onSave: (group: GroupChatModel, welcomeMessage: WelcomeMessageModel) => Promise<void>;
  show: boolean;
  coaches: CoachModel[];
}

const AddGroupDialog: React.FC<AddGroupDialogProps> = ({
  onClose,
  onSave,
  show,
  coaches,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedModeratorId, setSelectedModeratorId] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !description.trim() || !selectedModeratorId || !selectedFile) {
      toast.error('Please fill in all required fields and upload a group image');
      return;
    }

    setIsLoading(true);
    try {
      const groupId = generateId();
      const downloadUrl = await GroupsService.uploadGroupImage(selectedFile, groupId);

      const group: GroupChatModel = {
        id: groupId,
        name: name.trim(),
        description: description.trim(),
        photoUrl: downloadUrl,
        users: [],
        createdAt: Timestamp.now(),
        moderators: [selectedModeratorId],
        status: 'active',
        messageCount: 1,
      };

      const welcomeMessage: WelcomeMessageModel = {
        id: generateId(),
        content: "Hey everyone! 👋 Welcome to our group chat! This is a space for students and coach to connect, ask questions and support each other. Let's keep it positive, respectful, and fun! Looking forward to great discussions and learning together. 🚀😊",
        type: 'text',
        sentBy: selectedModeratorId,
        likes: [],
        status: 'sending',
        repliesCount: 0,
        isModerator: true,
      };

      await onSave(group, welcomeMessage);
      handleClose();
    } catch (error) {
      console.error('Error creating group:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setSelectedModeratorId('');
    setSelectedFile(null);
    setPreviewUrl('');
    onClose();
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onClose} id="addGroupModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-[calc(100vw-2rem)] mx-4 md:mx-0 md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Create New Group</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-500 dark:text-zink-200">Group Image</label>
              <div className="border-2 border-dashed border-slate-300 dark:border-zink-500 rounded-lg p-4 text-center">
                {previewUrl ? (
                  <div className="space-y-2">
                    <img
                      src={previewUrl}
                      alt="Group preview"
                      className="w-20 h-20 rounded-full object-cover mx-auto"
                    />
                    <p className="text-sm text-slate-600 dark:text-zink-300">
                      {selectedFile?.name}
                    </p>
                    <button
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl('');
                      }}
                      className="text-red-500 text-sm hover:text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="group-image"
                    />
                    <label
                      htmlFor="group-image"
                      className="cursor-pointer text-custom-500 hover:text-custom-600"
                    >
                      <div className="text-4xl mb-2">📷</div>
                      <p className="text-sm">Click to upload group image</p>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-500 dark:text-zink-200">Group Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-zink-500 rounded-md bg-white dark:bg-zink-600 text-slate-900 dark:text-zink-100 focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500"
                placeholder="Enter group name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-500 dark:text-zink-200">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 dark:border-zink-500 rounded-md bg-white dark:bg-zink-600 text-slate-900 dark:text-zink-100 focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500"
                placeholder="Enter group description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-500 dark:text-zink-200">Group Moderator</label>
              <select
                value={selectedModeratorId}
                onChange={(e) => setSelectedModeratorId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-zink-500 rounded-md bg-white dark:bg-zink-600 text-slate-900 dark:text-zink-100 focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500"
              >
                <option value="">Select Moderator</option>
                {coaches.map((coach) => (
                  <option key={coach.id} value={coach.id}>
                    {coach.fullName}
                  </option>
                ))}
              </select>
            </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <button
          onClick={handleClose}
          className="px-4 py-2 text-slate-600 dark:text-zink-300 border border-slate-300 dark:border-zink-500 rounded-md hover:bg-slate-50 dark:hover:bg-zink-600"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="px-4 py-2 bg-custom-500 text-white rounded-md hover:bg-custom-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          Create Group
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddGroupDialog;